import ReactEChartsCore from 'echarts-for-react/lib/core';
import { echarts, COLOR_PALETTE, baseChartOptions } from '../../shared/echarts-config';
import { ChartContainer } from '../../shared/ChartContainer';
import { EmptyState } from '../../shared/EmptyState';
import type { FpEngagementSeries } from '../../../../types/analytics.types';

type RegionalDistributionChartProps = {
  states: string[];
  series: FpEngagementSeries[];
};

const TITLE = 'Regional Distribution of Client Engagement';
const SUBTITLE = 'Engagement by state and type (state = provider service region)';

const TYPE_LABELS: Record<string, string> = {
  CONSULTATION_REQUEST: 'Consultation Requests',
  CALENDLY_BOOKING: 'Calendly Bookings',
  INTAKE_FORM_OPEN: 'Intake Form Opens',
  FIRST_MESSAGE: 'First Messages',
  RESPONDED_MESSAGE: 'Responded Messages',
  FA_PROVIDER_REFERRAL: 'FA Referrals',
  SP_PROVIDER_REFERRAL: 'SP Referrals',
  FA_FINANCIAL_ADVISOR_REFERRAL: 'FA to FA Referrals',
  SP_FINANCIAL_ADVISOR_REFERRAL: 'SP to FA Referrals',
  NETWORK_CONNECTION: 'Network Connections',
  MESSAGE_SENT: 'Messages Sent',
  RESOURCE_SHARED: 'Resources Shared',
  FP_SP_MESSAGE: 'FP to SP Messages',
  FP_SP_FIRST_MESSAGE: 'FP to SP First Messages',
  SP_OUTBOUND_FIRST_MESSAGE: 'SP Outbound First Messages',
};

// engagement_type is a free-form VARCHAR (no CHECK), so the DB can carry types
// not in the map above. Title-case anything unmapped instead of showing the raw
// SCREAMING_SNAKE enum.
const labelFor = (type: string): string =>
  TYPE_LABELS[type] ??
  type
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());

const buildOptions = (states: string[], series: FpEngagementSeries[]) => ({
  ...baseChartOptions,
  tooltip: { trigger: 'axis' as const, axisPointer: { type: 'shadow' as const } },
  legend: {
    bottom: 0,
    icon: 'circle',
    textStyle: { color: '#374151', fontSize: 12 },
    data: series.map((s) => labelFor(s.engagementType)),
  },
  grid: { left: '3%', right: '4%', bottom: 44, top: 12, containLabel: true },
  xAxis: {
    type: 'category' as const,
    data: states,
    axisLabel: { fontSize: 12, color: '#6b7280' },
    axisLine: { lineStyle: { color: '#e5e7eb' } },
  },
  yAxis: {
    type: 'value' as const,
    minInterval: 1,
    axisLabel: { fontSize: 12, color: '#6b7280' },
    splitLine: { lineStyle: { color: '#f3f4f6' } },
  },
  series: series.map((s, i) => ({
    name: labelFor(s.engagementType),
    type: 'bar',
    stack: 'total',
    data: s.counts,
    itemStyle: { color: COLOR_PALETTE[i % COLOR_PALETTE.length] },
  })),
});

export const RegionalDistributionChart = ({ states, series }: RegionalDistributionChartProps) => {
  const isEmpty =
    states.length === 0 ||
    series.length === 0 ||
    series.every((s) => s.counts.every((c) => c === 0));

  if (isEmpty) {
    return (
      <ChartContainer title={TITLE} subtitle={SUBTITLE}>
        <EmptyState message="No client engagement by region for the selected filters." />
      </ChartContainer>
    );
  }

  return (
    <ChartContainer title={TITLE} subtitle={SUBTITLE}>
      <ReactEChartsCore
        echarts={echarts}
        option={buildOptions(states, series)}
        style={{ height: 340 }}
        notMerge
        lazyUpdate
      />
    </ChartContainer>
  );
};
